<!-- eslint-disable vue/v-slot-style -->
<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useAgentsList } from 'dashboard/composables/useAgentsList';
import ContactDetailsItem from './ContactDetailsItem.vue';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import ConversationLabels from './labels/LabelBox.vue';
import { CONVERSATION_PRIORITY } from '../../../../shared/constants/messages';
import { CONVERSATION_EVENTS } from '../../../helper/AnalyticsHelper/events';
import { useTrack } from 'dashboard/composables';
// == Unichat Extension ==
import {
  getVisibility,
  getSectors,
  getSectorAgents,
  getSectorTeams,
  transferToSector,
  transferToTeam,
  getConversationUnichat,
} from 'dashboard/unichat/api/unichat.js';

export default {
  components: {
    ContactDetailsItem,
    MultiselectDropdown,
    ConversationLabels,
  },
  props: {
    conversationId: {
      type: [Number, String],
      required: true,
    },
  },
  setup() {
    const { agentsList } = useAgentsList();
    return { agentsList };
  },
  data() {
    return {
      // == Unichat Extension ==
      unichatPolicy: null,
      unichatSectors: [],
      unichatConversationSector: null,
      // modais
      showAgentModal: false,
      showSectorModal: false,
      showTeamModal: false,
      modalSelectedAgent: null,
      modalSelectedSector: null,
      modalSelectedTeam: null,
      sectorAgents: [],
      sectorAgentsLoading: false,
      sectorTeams: [],
      sectorTeamsLoading: false,
      priorityOptions: [
        {
          id: null,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.NONE'),
          icon: 'i-woot-priority-empty',
        },
        {
          id: CONVERSATION_PRIORITY.URGENT,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.URGENT'),
          icon: 'i-woot-priority-urgent',
        },
        {
          id: CONVERSATION_PRIORITY.HIGH,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.HIGH'),
          icon: 'i-woot-priority-high',
        },
        {
          id: CONVERSATION_PRIORITY.MEDIUM,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.MEDIUM'),
          icon: 'i-woot-priority-medium',
        },
        {
          id: CONVERSATION_PRIORITY.LOW,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.LOW'),
          icon: 'i-woot-priority-low',
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      currentUser: 'getCurrentUser',
      teams: 'teams/getTeams',
    }),
    isUnichatAdmin() {
      return this.currentUser?.role === 'administrator';
    },
    unichatAssignedSector() {
      return this.unichatConversationSector || null;
    },
    availableSectorsForModal() {
      return this.unichatSectors.filter(s => s.id !== this.unichatAssignedSector?.id);
    },
    assignedAgent: {
      get() {
        return this.currentChat.meta.assignee;
      },
      set(agent) {
        const agentId = agent ? agent.id : null;
        this.$store.dispatch('setCurrentChatAssignee', {
          conversationId: this.currentChat.id,
          assignee: agent,
        });
        this.$store.dispatch('assignAgent', {
          conversationId: this.currentChat.id,
          agentId,
        });
      },
    },
    assignedTeam: {
      get() {
        return this.currentChat.meta.team;
      },
      set(team) {
        const conversationId = this.currentChat.id;
        const teamId = team ? team.id : 0;
        this.$store.dispatch('setCurrentChatTeam', { team, conversationId });
        this.$store.dispatch('assignTeam', { conversationId, teamId });
      },
    },
    assignedPriority: {
      get() {
        return (
          this.priorityOptions.find(opt => opt.id === this.currentChat.priority) ||
          this.priorityOptions[0]
        );
      },
      set(priorityItem) {
        const conversationId = this.currentChat.id;
        const oldValue = this.currentChat?.priority;
        const priority = priorityItem.id;
        this.$store.dispatch('setCurrentChatPriority', { priority, conversationId });
        this.$store.dispatch('assignPriority', { conversationId, priority }).then(() => {
          useTrack(CONVERSATION_EVENTS.CHANGE_PRIORITY, {
            oldValue,
            newValue: priority,
            from: 'Conversation Sidebar',
          });
        });
      },
    },
  },
  async mounted() {
    const accountId = this._accountId();
    if (!accountId) return;
    try {
      if (this.isUnichatAdmin) {
        const { data } = await getSectors(accountId);
        this.unichatSectors = Array.isArray(data) ? data : (data?.payload ?? []);
      } else {
        const { data } = await getVisibility(accountId);
        this.unichatPolicy = data;
        this.unichatSectors = data?.visible_sectors ?? [];
      }
    } catch (err) {
      console.error('[Unichat] policy load error:', err?.message);
    }
    if (this.conversationId) this._loadSector(this.conversationId);
  },
  watch: {
    conversationId(newId) {
      if (newId) this._loadSector(newId);
    },
    // == Unichat Extension ==
    // unichatConversationSector e buscado de um endpoint separado (nao faz
    // parte do payload nativo da conversa), entao nao reage sozinho a
    // mudancas. updated_at muda em qualquer evento de transferencia/edicao
    // (websocket ou local), usado aqui so como gatilho para reconsultar.
    'currentChat.updated_at'() {
      if (this.conversationId) this._loadSector(this.conversationId);
    },
  },
  methods: {
    _accountId() {
      return (
        this.$route?.params?.accountId ||
        this.$store?.getters?.getCurrentAccountId ||
        window.location.pathname.match(/accounts\/(\d+)/)?.[1]
      );
    },
    async _loadSector(conversationId) {
      const accountId = this._accountId();
      if (!accountId) return;
      try {
        const { data } = await getConversationUnichat(accountId, conversationId);
        this.unichatConversationSector = data?.unichat_sector || null;
      } catch {
        this.unichatConversationSector = null;
      }
    },

    // === Modais ===
    async openAgentModal() {
      this.modalSelectedAgent = null;
      this.sectorAgents = [];
      this.sectorAgentsLoading = true;
      this.showAgentModal = true;
      const accountId = this._accountId();
      try {
        if (this.unichatAssignedSector) {
          const { data } = await getSectorAgents(accountId, this.unichatAssignedSector.id);
          this.sectorAgents = Array.isArray(data) ? data : (data?.payload ?? []);
        } else {
          this.sectorAgents = this.agentsList || [];
        }
      } catch {
        this.sectorAgents = [];
      } finally {
        this.sectorAgentsLoading = false;
      }
    },
    async openTeamModal() {
      if (!this.unichatAssignedSector) return;
      this.modalSelectedTeam = null;
      this.sectorTeams = [];
      this.sectorTeamsLoading = true;
      this.showTeamModal = true;
      const accountId = this._accountId();
      try {
        const { data } = await getSectorTeams(accountId, this.unichatAssignedSector.id);
        this.sectorTeams = Array.isArray(data) ? data : (data?.payload ?? []);
      } catch {
        this.sectorTeams = [];
      } finally {
        this.sectorTeamsLoading = false;
      }
    },
    openSectorModal() {
      this.modalSelectedSector = null;
      this.showSectorModal = true;
    },

    async confirmAgentTransfer() {
      if (!this.modalSelectedAgent) return;
      this.assignedAgent = this.modalSelectedAgent;
      this.showAgentModal = false;
      useAlert(`Agente atribuído: ${this.modalSelectedAgent.name}`);
    },
    async confirmTeamTransfer() {
      if (!this.modalSelectedTeam) return;
      const accountId = this._accountId();
      const conversationId = this.currentChat.id;
      try {
        await transferToTeam(
          accountId,
          conversationId,
          this.modalSelectedTeam.id,
          this.unichatAssignedSector.id
        );
        this.assignedAgent = null;
        this.$store.dispatch('setCurrentChatTeam', {
          team: this.modalSelectedTeam,
          conversationId,
        });
        this.showTeamModal = false;
        useAlert(`Conversa transferida para o setor ${this.modalSelectedTeam.name}`);
        this.leaveAfterTransfer(accountId);
      } catch (err) {
        const msg = err?.response?.data?.errors?.[0] || 'Não foi possível transferir';
        useAlert(msg);
      }
    },
    async confirmSectorTransfer() {
      if (!this.modalSelectedSector) return;
      const accountId = this._accountId();
      const conversationId = this.currentChat.id;
      try {
        await transferToSector(accountId, conversationId, this.modalSelectedSector.id);
        this.unichatConversationSector = this.modalSelectedSector;
        this.assignedAgent = null;
        this.$store.dispatch('setCurrentChatTeam', { team: null, conversationId });
        this.showSectorModal = false;
        useAlert(`Conversa transferida para ${this.modalSelectedSector.name}`);
        this.leaveAfterTransfer(accountId);
      } catch (err) {
        const msg = err?.response?.data?.errors?.[0] || 'Não foi possível transferir';
        useAlert(msg);
      }
    },

    // Após transferir, a conversa sai da posse do agente — volta para a lista.
    leaveAfterTransfer(accountId) {
      this.$store.dispatch('clearSelectedState');
      this.$router.push({ name: 'home', params: { accountId } });
    },

    onClickAssignPriority(selectedPriorityItem) {
      const isSame =
        this.assignedPriority && this.assignedPriority.id === selectedPriorityItem.id;
      this.assignedPriority = isSame ? this.priorityOptions[0] : selectedPriorityItem;
    },
  },
};
</script>

<template>
  <div class="unichat-panel">

    <!-- === Bloco de informações === -->
    <div class="unichat-info-block">
      <div class="unichat-info-row">
        <span class="unichat-info-label">Agente</span>
        <span class="unichat-info-value" :class="{ 'unichat-info-empty': !assignedAgent }">
          {{ assignedAgent?.name || 'Não atribuído' }}
        </span>
      </div>
      <div class="unichat-info-row">
        <span class="unichat-info-label">Setor</span>
        <span class="unichat-info-value" :class="{ 'unichat-info-empty': !assignedTeam }">
          {{ assignedTeam?.name || 'Nenhum' }}
        </span>
      </div>
      <div v-if="unichatSectors.length" class="unichat-info-row">
        <span class="unichat-info-label">Secretaria</span>
        <span
          class="unichat-info-value"
          :class="{ 'unichat-info-empty': !unichatAssignedSector }"
        >
          {{ unichatAssignedSector?.name || 'Não atribuída' }}
        </span>
      </div>
    </div>

    <!-- === Botões de transferência === -->
    <div class="unichat-transfer-buttons">
      <button class="unichat-btn-transfer" @click="openAgentModal">
        Transferir para Agente
      </button>
      <button
        class="unichat-btn-transfer"
        :disabled="!unichatAssignedSector"
        @click="openTeamModal"
      >
        Transferir para Setor
      </button>
      <button
        v-if="unichatSectors.length"
        class="unichat-btn-transfer"
        @click="openSectorModal"
      >
        Transferir para Secretaria
      </button>
    </div>

    <!-- === Prioridade === -->
    <div class="unichat-priority-block">
      <ContactDetailsItem compact :title="$t('CONVERSATION.PRIORITY.TITLE')" />
      <MultiselectDropdown
        :options="priorityOptions"
        :selected-item="assignedPriority"
        :multiselector-title="$t('CONVERSATION.PRIORITY.TITLE')"
        :multiselector-placeholder="$t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SELECT_PLACEHOLDER')"
        :no-search-result="$t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.NO_RESULTS')"
        :input-placeholder="$t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.INPUT_PLACEHOLDER')"
        @select="onClickAssignPriority"
      />
    </div>

    <!-- === Etiquetas === -->
    <ContactDetailsItem
      compact
      :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_LABELS')"
    />
    <ConversationLabels :conversation-id="conversationId" />

    <!-- ======= Modal: Transferir para Agente ======= -->
    <Teleport to="body">
      <div v-if="showAgentModal" class="unichat-overlay" @click.self="showAgentModal = false">
        <div class="unichat-modal" role="dialog">
          <div class="unichat-modal-header">
            <span class="unichat-modal-title">Transferir para Agente</span>
          </div>
          <p class="unichat-modal-subtitle">
            Selecione o agente
            <span v-if="unichatAssignedSector">
              da <strong>{{ unichatAssignedSector.name }}</strong>
            </span>
          </p>
          <div v-if="sectorAgentsLoading" class="unichat-loading">Carregando agentes...</div>
          <div v-else class="unichat-option-list">
            <div
              v-for="a in sectorAgents"
              :key="a.id"
              class="unichat-option-item"
              :class="{ 'unichat-option-item--selected': modalSelectedAgent?.id === a.id }"
              @click="modalSelectedAgent = a"
            >
              <span class="unichat-agent-name">{{ a.name }}</span>
              <span
                class="unichat-agent-status"
                :class="`unichat-status--${a.availability_status}`"
              />
            </div>
            <div v-if="!sectorAgents.length" class="unichat-empty">Nenhum agente encontrado</div>
          </div>
          <div class="unichat-modal-footer">
            <button class="unichat-btn-cancel" @click="showAgentModal = false">Cancelar</button>
            <button
              class="unichat-btn-confirm"
              :disabled="!modalSelectedAgent"
              @click="confirmAgentTransfer"
            >
              Atribuir
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ======= Modal: Transferir para outro Setor ======= -->
    <Teleport to="body">
      <div v-if="showTeamModal" class="unichat-overlay" @click.self="showTeamModal = false">
        <div class="unichat-modal" role="dialog">
          <div class="unichat-modal-header">
            <span class="unichat-modal-title">Transferir para outro Setor</span>
          </div>
          <p class="unichat-modal-subtitle">
            Secretaria: <strong>{{ unichatAssignedSector?.name }}</strong>
          </p>
          <div v-if="sectorTeamsLoading" class="unichat-loading">Carregando setores...</div>
          <div v-else class="unichat-option-list">
            <div
              v-for="t in sectorTeams"
              :key="t.id"
              class="unichat-option-item"
              :class="{ 'unichat-option-item--selected': modalSelectedTeam?.id === t.id }"
              @click="modalSelectedTeam = t"
            >
              {{ t.name }}
            </div>
            <div v-if="!sectorTeams.length" class="unichat-empty">Nenhum setor encontrado</div>
          </div>
          <div class="unichat-modal-footer">
            <button class="unichat-btn-cancel" @click="showTeamModal = false">Cancelar</button>
            <button
              class="unichat-btn-confirm"
              :disabled="!modalSelectedTeam"
              @click="confirmTeamTransfer"
            >
              Transferir
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ======= Modal: Transferir para outra Secretaria ======= -->
    <Teleport to="body">
      <div v-if="showSectorModal" class="unichat-overlay" @click.self="showSectorModal = false">
        <div class="unichat-modal" role="dialog">
          <div class="unichat-modal-header">
            <span class="unichat-modal-title">Transferir para outra Secretaria</span>
          </div>
          <p class="unichat-modal-subtitle">Selecione a secretaria de destino:</p>
          <div class="unichat-option-list">
            <div
              v-for="s in availableSectorsForModal"
              :key="s.id"
              class="unichat-option-item"
              :class="{ 'unichat-option-item--selected': modalSelectedSector?.id === s.id }"
              @click="modalSelectedSector = s"
            >
              {{ s.name }}
            </div>
            <div v-if="!availableSectorsForModal.length" class="unichat-empty">
              Nenhuma outra secretaria disponível
            </div>
          </div>
          <div class="unichat-modal-footer">
            <button class="unichat-btn-cancel" @click="showSectorModal = false">Cancelar</button>
            <button
              class="unichat-btn-confirm"
              :disabled="!modalSelectedSector"
              @click="confirmSectorTransfer"
            >
              Transferir
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
/* == Unichat Panel == */
.unichat-panel {
  padding: 4px 0 8px;
}

/* Info block */
.unichat-info-block {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 0 4px 10px;
  border-bottom: 1px solid rgba(255,255,255,0.07);
  margin-bottom: 10px;
}

.unichat-info-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 2px;
}

.unichat-info-label {
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
  flex-shrink: 0;
  min-width: 72px;
}

.unichat-info-value {
  font-size: 12px;
  color: #cbd5e1;
  font-weight: 500;
  text-align: right;
  max-width: 170px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.unichat-info-empty {
  color: #475569;
  font-style: italic;
  font-weight: 400;
}

/* Transfer buttons */
.unichat-transfer-buttons {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 0 4px 12px;
  border-bottom: 1px solid rgba(255,255,255,0.07);
  margin-bottom: 10px;
}

.unichat-btn-transfer {
  width: 100%;
  padding: 8px 10px;
  font-size: 12px;
  font-weight: 600;
  color: #10B981;
  background: rgba(16,185,129,.09);
  border: 1px solid rgba(16,185,129,.25);
  border-radius: 9px;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s;
  letter-spacing: 0.01em;
}

.unichat-btn-transfer:hover:not(:disabled) {
  background: rgba(16,185,129,.18);
}

.unichat-btn-transfer:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

/* Priority block */
.unichat-priority-block {
  margin-bottom: 4px;
}

/* Overlay */
.unichat-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Modal */
.unichat-modal {
  background: #1e2430;
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 10px;
  padding: 24px;
  width: 340px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.5);
}

.unichat-modal-header {
  display: flex;
  align-items: center;
}

.unichat-modal-title {
  font-size: 15px;
  font-weight: 600;
  color: #e2e8f0;
}

.unichat-modal-subtitle {
  font-size: 13px;
  color: #94a3b8;
  margin: 0;
}

.unichat-modal-subtitle strong {
  color: #cbd5e1;
}

/* Option list */
.unichat-option-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow-y: auto;
  max-height: 260px;
}

.unichat-option-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border-radius: 6px;
  font-size: 13px;
  color: #cbd5e1;
  cursor: pointer;
  background: rgba(255,255,255,0.04);
  transition: background 0.12s;
  user-select: none;
}

.unichat-option-item:hover {
  background: rgba(31,147,255,0.15);
  color: #e2e8f0;
}

.unichat-option-item--selected {
  background: rgba(31,147,255,0.25);
  color: #1f93ff;
  font-weight: 500;
}

.unichat-agent-name {
  flex: 1;
}

.unichat-agent-status {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.unichat-status--online    { background: #22c55e; }
.unichat-status--busy      { background: #f59e0b; }
.unichat-status--offline   { background: #64748b; }

.unichat-loading,
.unichat-empty {
  padding: 16px;
  text-align: center;
  font-size: 13px;
  color: #64748b;
}

/* Modal footer */
.unichat-modal-footer {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  padding-top: 4px;
  border-top: 1px solid rgba(255,255,255,0.07);
}

.unichat-btn-cancel {
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 500;
  color: #94a3b8;
  background: transparent;
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.12s;
}

.unichat-btn-cancel:hover {
  background: rgba(255,255,255,0.06);
  color: #e2e8f0;
}

.unichat-btn-confirm {
  padding: 8px 20px;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  background: #1f93ff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.12s;
}

.unichat-btn-confirm:hover:not(:disabled) {
  background: #1a7fe0;
}

.unichat-btn-confirm:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
</style>
